import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { buildApiUrl, getApiBaseUrl } from '../../config/apiConfig';
import { useCart } from '../../context/CartContext';
import { useNotifications } from '../../context/NotificationContext';
import './ShopPage.css';
import { log, logError } from '../../utils/logger';
import { formatARS } from '../../utils/currency';
import { computeItemUnitPrice } from '../../utils/itemPricing';
import { useStoreSettings } from '../../hooks/useStoreSettings';
import ShippingNotice from '../../components/ShippingNotice/ShippingNotice';

const resolveImageUrl = (source) => {
  if (!source) return null;
  const sanitized = String(source).replace(/\\/g, '/').replace(/\s/g, '%20');
  if (/^https?:\/\//i.test(sanitized)) return sanitized;
  const base = getApiBaseUrl();
  if (sanitized.startsWith('/')) return `${base}${sanitized}`;
  if (sanitized.startsWith('uploads/')) return `${base}/${sanitized}`;
  return `${base}/uploads/${sanitized}`;
};

const ShopPage = () => {
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { notify } = useNotifications();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [categories, setCategories] = useState([]);
  const [addingToCart, setAddingToCart] = useState(null);
  const { settings } = useStoreSettings();

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const response = await fetch(buildApiUrl('/api/products/public/items'));
      if (!response.ok) throw new Error('Error al cargar productos');
      const data = await response.json();
      setProducts(data.products || []);
      
      // Extraer categorías únicas
      const uniqueCategories = [...new Set(
        (data.products || [])
          .map(p => p.item?.categoria)
          .filter(Boolean)
      )];
      setCategories(uniqueCategories);
    } catch (err) {
      logError('[ShopPage] Error fetching products:', err);
      notify({ message: 'Error al cargar productos', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const filteredProducts = useMemo(() => {
    return products.filter(product => {
      const matchesSearch = !search || 
        product.name?.toLowerCase().includes(search.toLowerCase()) ||
        product.description?.toLowerCase().includes(search.toLowerCase()) ||
        product.item?.nombre?.toLowerCase().includes(search.toLowerCase());
      
      const matchesCategory = !selectedCategory || product.item?.categoria === selectedCategory;
      
      return matchesSearch && matchesCategory;
    });
  }, [products, search, selectedCategory]);

  const handleAddToCart = async (product) => {
    const vars = product.item?.variantes || [];
    const hasVariants = Array.isArray(vars) && vars.length > 0;
    const stockMadre = product.item?.stockMadre ?? product.item?.stock ?? 0;
    const stockVariantes =
      product.item?.stockVariantes ??
      vars.reduce((s, v) => s + (v.stock_variante ?? 0), 0);

    if (hasVariants && stockMadre <= 0) {
      if (stockVariantes > 0) {
        navigate(`/shop/${product.referenceId}`);
        return;
      }
      notify({ message: 'Este producto no tiene stock disponible', type: 'warning' });
      return;
    }

    if (!hasVariants && stockMadre <= 0) {
      notify({ message: 'Este producto no tiene stock disponible', type: 'warning' });
      return;
    }

    const priced = computeItemUnitPrice({
      listPrice: product.price,
      itemOferta: product.item?.oferta,
      productDiscountPercent: product.discountPercent,
    });

    setAddingToCart(product.productId);
    try {
      const cartItem = {
        id: product.productId,
        productId: product.productId,
        name: product.name || product.item?.nombre,
        price: priced.finalPrice,
        image: product.item?.imagenes?.[0] || null,
        productType: 'item',
        type: 'item',
        referenceId: product.referenceId,
        quantity: 1,
      };

      const success = await addToCart(cartItem);
      if (success) {
        notify({ message: 'Producto agregado al carrito', type: 'success' });
      }
    } catch (err) {
      logError('[ShopPage] Error adding to cart:', err);
      notify({ message: 'Error al agregar al carrito', type: 'error' });
    } finally {
      setAddingToCart(null);
    }
  };

  if (loading) {
    return (
      <div className="shop-page">
        <div className="loading-container">
          <i className="fas fa-spinner fa-spin"></i>
          <p>Cargando productos...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="shop-page">
      {/* Hero Section */}
      <div className="shop-hero">
        <div className="shop-hero-content">
          <div className="shop-hero-text">
            <span className="shop-hero-badge">
              <i className="fas fa-shopping-bag"></i>
              Tienda Odontológica
            </span>
            <h1 className="shop-hero-title">
              Equipamiento y Materiales <span className="shop-hero-title-accent">Profesionales</span>
            </h1>
            <p className="shop-hero-description">
              Todo lo que necesitás para tu consultorio dental. <strong>Instrumental de precisión</strong>, 
              materiales de última generación y equipamiento profesional para brindar la mejor atención a tus pacientes.
            </p>
            <div className="shop-hero-features">
              <div className="shop-hero-feature">
                <i className="fas fa-check-circle"></i>
                <span>Calidad Certificada</span>
              </div>
              <div className="shop-hero-feature">
                <i className="fas fa-check-circle"></i>
                <span>Coordinación por WhatsApp</span>
              </div>
              <div className="shop-hero-feature">
                <i className="fas fa-check-circle"></i>
                <span>Garantía Oficial</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="shop-filters-section">
        <div className="shop-filters-container">
          <ShippingNotice
            variant="shop"
            freeShippingThreshold={settings.freeShippingThreshold}
          />
          <div className="shop-search-box">
            <i className="fas fa-search"></i>
            <input
              type="text"
              placeholder="Buscar productos..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="shop-search-input"
            />
          </div>
          {categories.length > 0 && (
            <div className="shop-category-filter">
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="shop-category-select"
              >
                <option value="">Todas las categorías</option>
                {categories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
          )}
        </div>
      </div>

      {/* Products Grid */}
      <div className="shop-products-section">
        <div className="shop-products-container">
          {filteredProducts.length > 0 && (
            <div className="shop-products-header">
              <h2 className="shop-products-title">
                <i className="fas fa-box"></i>
                Productos Disponibles
              </h2>
              <p className="shop-products-count">
                {filteredProducts.length} {filteredProducts.length === 1 ? 'producto encontrado' : 'productos encontrados'}
              </p>
            </div>
          )}
          {filteredProducts.length === 0 ? (
            <div className="shop-empty-state">
              <i className="fas fa-box-open"></i>
              <h2>No se encontraron productos</h2>
              <p>Intenta ajustar tus filtros de búsqueda</p>
            </div>
          ) : (
            <div className="shop-products-grid">
              {filteredProducts.map((product) => {
                // Obtener la primera imagen disponible
                let imageUrl = null;
                if (product.item?.imagenes && Array.isArray(product.item.imagenes) && product.item.imagenes.length > 0) {
                  const firstImage = product.item.imagenes[0];
                  // Si es un string, usarlo directamente; si es un objeto, usar image_url
                  if (typeof firstImage === 'string') {
                    imageUrl = resolveImageUrl(firstImage);
                  } else if (firstImage && typeof firstImage === 'object') {
                    imageUrl = resolveImageUrl(firstImage.image_url || firstImage.url || firstImage);
                  }
                }
                
                // Debug: Log para ver qué imágenes tenemos
                if (process.env.NODE_ENV === 'development' && product.item?.imagenes) {
                  log(`[ShopPage] Product ${product.productId} - Imágenes:`, product.item.imagenes, 'URL resuelta:', imageUrl);
                }
                
                const hasVariants = Array.isArray(product.item?.variantes) && product.item.variantes.length > 0;
                const stockMadre = product.item?.stockMadre ?? product.item?.stock ?? 0;
                const stockVariantes =
                  product.item?.stockVariantes ??
                  (hasVariants
                    ? product.item.variantes.reduce((s, v) => s + (v.stock_variante ?? 0), 0)
                    : 0);
                const hasStock = stockMadre > 0 || stockVariantes > 0;
                const canAddMother = stockMadre > 0;
                const isAdding = addingToCart === product.productId;

                const priced = computeItemUnitPrice({
                  listPrice: product.price,
                  itemOferta: product.item?.oferta,
                  productDiscountPercent: product.discountPercent,
                });
                const displayListPrice = priced.listPrice;
                const finalPrice = priced.finalPrice;
                const displayOfferPct = priced.offerPercent;
                const showOffer = priced.hasOffer;
                const displayStock = stockMadre > 0 ? stockMadre : stockVariantes;

                return (
                  <div
                    key={product.productId}
                    className="shop-product-card"
                    onClick={() => navigate(`/shop/${product.referenceId}`)}
                  >
                    <div className="shop-product-image">
                      <div className="shop-product-image-frame">
                        {imageUrl ? (
                          <img src={imageUrl} alt={product.name || product.item?.nombre} />
                        ) : (
                          <div className="shop-product-placeholder">
                            <i className="fas fa-box"></i>
                          </div>
                        )}
                      </div>
                      {!hasStock && (
                        <div className="shop-product-out-of-stock">
                          <span>Sin Stock</span>
                        </div>
                      )}
                      {showOffer && (
                        <div className="shop-product-offer-badge">
                          <span>{displayOfferPct}% OFF</span>
                        </div>
                      )}
                    </div>
                    <div className="shop-product-content">
                      <h3 className="shop-product-title">{product.name || product.item?.nombre}</h3>
                      <p className="shop-product-description">
                        {product.description || product.item?.descripcionBreve || 'Sin descripción'}
                      </p>
                      <div className="shop-product-content-actions">
                        <div className="shop-product-footer">
                          <div className="shop-product-price">
                            {showOffer ? (
                              <>
                                <span className="shop-product-price-old">{formatARS(displayListPrice)}</span>
                                <span className="shop-product-price-new">
                                  {formatARS(finalPrice)}
                                </span>
                              </>
                            ) : (
                              <span className="shop-product-price-current">{formatARS(finalPrice)}</span>
                            )}
                          </div>
                          <div className="shop-product-stock">
                            <i className="fas fa-warehouse"></i>
                            <span>{displayStock} disponibles</span>
                          </div>
                        </div>
                        <button
                          type="button"
                          className={`shop-product-add-btn ${!hasStock ? 'disabled' : ''}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (hasStock) handleAddToCart(product);
                          }}
                          disabled={!hasStock || isAdding}
                        >
                          {isAdding ? (
                            <>
                              <i className="fas fa-spinner fa-spin"></i>
                              {hasVariants && !canAddMother ? 'Abriendo…' : 'Agregando...'}
                            </>
                          ) : (
                            <>
                              <i
                                className={`fas ${
                                  hasVariants && !canAddMother ? 'fa-sliders-h' : 'fa-cart-plus'
                                }`}
                              ></i>
                              {hasVariants && !canAddMother && hasStock
                                ? 'Ver opciones'
                                : 'Agregar al Carrito'}
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ShopPage;

